import { Empty, Input, notification } from 'antd'
import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import Loading from '../../../components/shared-components/Loading'
import { UserRoleEnum } from '../../../services/CompanyService'
import { selectSelectedCompanyId, selectUserRole } from '../../../store/companies/selectors'
import { setTableOrderItems } from '../../../store/order-items-table/reducer'
import {
  IntegratedDeliveryNote,
  mapGetOrderResponseToOrder,
  mapGetOrderResponseToPunchOut,
  Order,
  OrderItem,
  PunchOutData,
} from '../../../utils/orders.util'
import { OrderErpTable, OrderIdnTable, OrderItemTimelineModal, OrderTableHeader } from './components'
import * as Sentry from '@sentry/react'
import { Tabs } from '@mantine/core'
import { setIntegratedDeliveryNotes } from '../../../store/idn-table/reducer'
import { useIntl } from 'react-intl'
import { selectIntegratedDeliveryNotes } from '../../../store/idn-table/selector'
import { selectTableOrderItems } from '../../../store/order-items-table/selectors'
import { OrderService } from '../../../services'
import { GetOrderResponse } from '../../../services/OrderService'
import { ProposalFilesView } from '../proposal/content/proposal-files/proposal-files-view'

type OrderViewRouteParams = {
  orderId: string;
}

export const OrderView = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { orderId } = useParams<OrderViewRouteParams>()
  const { formState: { isSubmitting } } = useForm()
  const [isLoading, setLoading] = useState(true)
  const [isNotifying, setNotifying] = useState(false)
  const [punchOut, setPunchOut] = useState<PunchOutData|null>(null)
  const [order, setOrder] = useState<Order|null>(null)
  const userRole: UserRoleEnum|null = useSelector(selectUserRole)
  const selectedCompanyId: string|null = useSelector(selectSelectedCompanyId)
  const [activeTab, setActiveTab] = useState(0)
  const orderItems: Array<OrderItem> = useSelector(selectTableOrderItems)
  const hasOrderItems = Boolean(orderItems?.length)
  const idns: Array<IntegratedDeliveryNote> = useSelector(selectIntegratedDeliveryNotes)
  const hasInds = Boolean(idns?.length)

  async function notifyOnCheckout(event: React.FormEvent) {
    setNotifying(true)
    try {
      await OrderService.punchoutCatalogsCheckout(orderId)
    }
    catch (error) {
      // do not show error to user - its is more critical to submit form
      Sentry.captureException(error)
    }
    setNotifying(false)
  }

  const getOrder = useCallback(() => {
    setLoading(true)

    OrderService
      .getOrder(orderId)
      .then((response: GetOrderResponse) => {
        const orderData: Order|null = mapGetOrderResponseToOrder(response)
        setOrder(orderData)
        const punchOutData: PunchOutData|null = mapGetOrderResponseToPunchOut(response)
        setPunchOut(punchOutData)
        dispatch(setTableOrderItems(orderData?.items || []))
        dispatch(setIntegratedDeliveryNotes(orderData?.integratedDeliveryNotes ?? []))
      })
      .catch((error: Error) => {
        notification.error({
          message: 'Ошибка при получении данных по сделке',
          description: error.message,
          duration: 5,
        })
        Sentry.captureException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dispatch, orderId])

  useEffect(() => {
    if (!userRole || (userRole === UserRoleEnum.Sales && !selectedCompanyId)) {
      return
    }

    getOrder()
  }, [getOrder, userRole, selectedCompanyId])

  return (
    <Suspense fallback={<Loading cover='content'/>}>
      <div className='order-view'>
        {isLoading && <Loading cover='content'/>}
        {!isLoading && !order && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{ height: 200 }}
            description={
              <span>К сожалению, заявка №{orderId} не найдена</span>
            }
            style={{ color: '#455560' }}
          >
            Вы можете создать
            {' '}
            <Link to={'/new'}>новую заявку</Link>, найти заявку в
            {' '}
            <Link to={'/orders/proposals'}>КП</Link>, в списке
            {' '}
            <Link to={'/orders/active'}>активных</Link> или в
            {' '}
            <Link to={'/orders/archive'}>архиве</Link>
          </Empty>
        )}
        {!isLoading && order && (
          <>
            <OrderTableHeader order={order}/>
            <div
              className='erp-order-table'
              style={{ margin: '0 20px' }}
            >
              <Tabs
                active={activeTab}
                onTabChange={setActiveTab}
              >
                <Tabs.Tab
                  label={intl.messages['orderPage.tabs.positions']}
                >
                  {!hasOrderItems && (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                  {hasOrderItems && (
                    <>
                      <OrderErpTable order={order}/>
                      <OrderItemTimelineModal/>
                    </>
                  )}
                  {punchOut && (
                    <form
                      action={punchOut.url}
                      method='POST'
                      encType='application/x-www-form-urlencoded'
                      onSubmit={notifyOnCheckout}
                    >
                      <input
                        type='hidden'
                        name='cxml-base64'
                        value={punchOut.order_message}
                        defaultValue={punchOut.order_message}
                      />
                      <Input type='submit' value='Checkout' disabled={isSubmitting || isNotifying}/>
                    </form>
                  )}
                </Tabs.Tab>
                {hasInds && (
                  <Tabs.Tab
                    label={`${intl.messages['orderPage.tabs.idn']} (${order?.integratedDeliveryNotes?.length ?? 0})`}
                  >
                    <OrderIdnTable/>
                  </Tabs.Tab>
                )}
                {order && order.customerDocs?.length > 0 && (
                  <Tabs.Tab
                    label={`${intl.messages['orderPage.tabs.docs']} (${order?.customerDocs?.length ?? 0})`}
                  >
                    <ProposalFilesView
                      proposalFiles={[...order.customerDocs]}
                      isLoading={isLoading}
                      onAttachFiles={() => {
                      }}
                      onRemoveFile={() => {
                      }}
                      canEdit={false}
                    />
                  </Tabs.Tab>
                )}
              </Tabs>
            </div>
          </>
        )}
      </div>
    </Suspense>
  )
}
